import { alpha, createTheme } from '@mui/material/styles';
import type { PaletteMode } from '@mui/material';

/** Colores de marca reutilizables */
export const brandColors = {
  primary: '#0d47a1',
  primaryLight: '#1565c0',
  primaryDark: '#0a3a82',
  secondary: '#00695c',
  secondaryLight: '#00897b',
  background: '#f5f7fa',
  accent: '#e65100',
};

const darkPrimary = { main: '#90caf9', light: '#bbdefb', dark: '#64b5f6', contrastText: '#0a1929' };
const darkSecondary = { main: '#4db6ac', light: '#80cbc4', dark: '#26a69a', contrastText: '#00251a' };

export function createAppTheme(mode: PaletteMode) {
  const isDark = mode === 'dark';

  return createTheme({
    palette: {
      mode,
      primary: isDark ? darkPrimary : {
        main: brandColors.primary,
        light: brandColors.primaryLight,
        dark: brandColors.primaryDark,
        contrastText: '#ffffff',
      },
      secondary: isDark ? darkSecondary : {
        main: brandColors.secondary,
        light: brandColors.secondaryLight,
        dark: '#004d40',
        contrastText: '#ffffff',
      },
      success: { main: '#2e7d32', light: '#4caf50', dark: '#1b5e20', contrastText: '#fff' },
      warning: { main: '#f57c00', light: '#ff9800', dark: '#e65100', contrastText: '#fff' },
      error: { main: '#c62828', light: '#ef5350', dark: '#b71c1c', contrastText: '#fff' },
      info: { main: '#0277bd', light: '#29b6f6', dark: '#01579b', contrastText: '#fff' },
      background: isDark
        ? { default: '#0f1419', paper: '#1a222c' }
        : { default: brandColors.background, paper: '#ffffff' },
      divider: isDark ? 'rgba(255,255,255,0.12)' : 'rgba(0,0,0,0.09)',
      text: isDark
        ? { primary: '#e8eaed', secondary: '#b0b8c4' }
        : { primary: '#1a1a1a', secondary: '#5c5c5c' },
      action: isDark
        ? { active: '#90caf9', hover: 'rgba(144, 202, 249, 0.12)' }
        : { active: brandColors.primary, hover: 'rgba(13, 71, 161, 0.06)' },
    },
    typography: {
      fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
      h4: { fontWeight: 600 },
      h5: { fontWeight: 600 },
      h6: { fontWeight: 600 },
      button: { textTransform: 'none', fontWeight: 600 },
    },
    shape: { borderRadius: 8 },
    components: {
      MuiButton: {
        defaultProps: { disableElevation: true },
        styleOverrides: {
          root: { borderRadius: 8 },
          containedPrimary: ({ theme }) => ({
            boxShadow: 'none',
            background: theme.palette.mode === 'dark'
              ? `linear-gradient(180deg, ${theme.palette.primary.light} 0%, ${theme.palette.primary.main} 100%)`
              : `linear-gradient(180deg, ${brandColors.primaryLight} 0%, ${brandColors.primary} 100%)`,
            '&:hover': {
              boxShadow: `0 2px 10px ${alpha(theme.palette.primary.main, theme.palette.mode === 'dark' ? 0.45 : 0.35)}`,
              filter: 'brightness(1.05)',
            },
          }),
          outlined: { borderWidth: 2, '&:hover': { borderWidth: 2 } },
          outlinedPrimary: ({ theme }) => ({
            borderColor: theme.palette.primary.main,
          }),
          sizeLarge: { py: 1.1, px: 2.5 },
        },
      },
      MuiChip: {
        styleOverrides: {
          root: { fontWeight: 600, borderRadius: 8 },
          colorPrimary: ({ theme }) => ({
            backgroundColor: alpha(theme.palette.primary.main, theme.palette.mode === 'dark' ? 0.22 : 0.12),
            color: theme.palette.mode === 'dark' ? theme.palette.primary.light : brandColors.primary,
            border: '1px solid',
            borderColor: alpha(theme.palette.primary.main, theme.palette.mode === 'dark' ? 0.45 : 0.35),
          }),
          colorSecondary: ({ theme }) => ({
            backgroundColor: alpha(theme.palette.secondary.main, theme.palette.mode === 'dark' ? 0.22 : 0.12),
            color: theme.palette.mode === 'dark' ? theme.palette.secondary.light : brandColors.secondary,
            border: '1px solid',
            borderColor: alpha(theme.palette.secondary.main, theme.palette.mode === 'dark' ? 0.45 : 0.35),
          }),
          colorSuccess: ({ theme }) => ({
            backgroundColor: alpha(theme.palette.success.main, theme.palette.mode === 'dark' ? 0.2 : 0.1),
            color: theme.palette.mode === 'dark' ? theme.palette.success.light : '#1b5e20',
          }),
          colorWarning: ({ theme }) => ({
            backgroundColor: alpha(theme.palette.warning.main, theme.palette.mode === 'dark' ? 0.2 : 0.12),
            color: theme.palette.mode === 'dark' ? theme.palette.warning.light : '#e65100',
          }),
          colorInfo: ({ theme }) => ({
            backgroundColor: alpha(theme.palette.info.main, theme.palette.mode === 'dark' ? 0.2 : 0.1),
            color: theme.palette.mode === 'dark' ? theme.palette.info.light : '#01579b',
          }),
          colorError: ({ theme }) => ({
            backgroundColor: alpha(theme.palette.error.main, theme.palette.mode === 'dark' ? 0.2 : 0.1),
            color: theme.palette.mode === 'dark' ? theme.palette.error.light : '#b71c1c',
          }),
        },
      },
      MuiAppBar: {
        defaultProps: { elevation: 0, color: 'inherit' },
        styleOverrides: {
          root: ({ theme }) => ({
            backgroundColor: theme.palette.background.paper,
            color: theme.palette.text.primary,
            backgroundImage: 'none',
            boxShadow: theme.palette.mode === 'dark'
              ? '0 1px 0 rgba(255,255,255,0.08)'
              : '0 1px 0 rgba(0,0,0,0.08)',
            borderBottom: 'none',
            borderRadius: 0,
          }),
        },
      },
      MuiPaper: {
        styleOverrides: {
          root: ({ theme }) => ({
            borderRadius: 0,
            border: '1px solid',
            borderColor: theme.palette.divider,
            boxShadow: theme.palette.mode === 'dark'
              ? '0 1px 4px rgba(0,0,0,0.45)'
              : '0 1px 2px rgba(0,0,0,0.04)',
          }),
        },
      },
      MuiCard: {
        styleOverrides: {
          root: { borderRadius: 0 },
        },
      },
      MuiDialog: {
        styleOverrides: {
          paper: { borderRadius: 12 },
        },
      },
      MuiAvatar: {
        defaultProps: { variant: 'square' },
        styleOverrides: {
          root: { borderRadius: 0 },
          rounded: { borderRadius: 0 },
          square: { borderRadius: 0 },
        },
      },
      MuiOutlinedInput: {
        styleOverrides: {
          notchedOutline: { borderRadius: 8 },
          root: { borderRadius: 8 },
        },
      },
      MuiFilledInput: {
        styleOverrides: {
          root: { borderRadius: 8 },
        },
      },
      MuiInput: {
        styleOverrides: {
          root: { borderRadius: 8 },
        },
      },
      MuiInputBase: {
        styleOverrides: {
          root: { borderRadius: 8 },
        },
      },
      MuiIconButton: {
        styleOverrides: {
          root: { borderRadius: '50%' },
          colorPrimary: ({ theme }) => ({ color: theme.palette.primary.main }),
          colorSecondary: ({ theme }) => ({ color: theme.palette.secondary.main }),
        },
      },
      MuiDrawer: {
        styleOverrides: {
          paper: { borderRadius: 0 },
        },
      },
      MuiMenu: {
        styleOverrides: {
          paper: { borderRadius: 8 },
        },
      },
      MuiAlert: {
        styleOverrides: {
          root: { borderRadius: 8 },
          standardSuccess: { borderRadius: 8 },
          standardError: { borderRadius: 8 },
          standardWarning: { borderRadius: 8 },
          standardInfo: { borderRadius: 8 },
        },
      },
    },
  });
}
